import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LuLoaderCircle, LuCircleAlert, LuMail } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import { apiFetch } from '../services/api';

/**
 * /esqueci-senha — pedir o link de redefinição.
 *
 * Nasceu em 18/09 porque **não havia saída nenhuma** para quem esquecia a senha:
 * nem tela, nem endpoint, nem e-mail. Pior, o cadastro já oferecia "recuperar a
 * senha" apontando para o /login, que não recuperava nada.
 */
const EsqueciSenhaPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const location = useLocation();

  /* O e-mail já digitado no login vem junto — não se pede duas vezes. */
  const emailInicial = (location.state as { email?: string } | null)?.email ?? '';

  const [email, setEmail] = useState(emailInicial);
  const [carregando, setCarregando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (carregando) return;

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErro('Confira o e-mail: falta algo nele.');
      document.getElementById('rec-email')?.focus();
      return;
    }

    setErro(null);
    setCarregando(true);

    try {
      await apiFetch(`${apiUrl}/api/Auth/SolicitarRedefinicao`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      /* A resposta é a mesma exista ou não a conta — inclusive quando a chamada
         falha por conta inexistente. Ver o comentário da tela de sucesso. */
      setEnviado(true);
    } catch {
      setErro('Não conseguimos falar com o servidor. Tente de novo em instantes.');
    } finally {
      setCarregando(false);
    }
  };

  if (enviado) {
    return (
      <AuthShell>
        <h1 className="au-h1">Verifique o seu e-mail</h1>
        {/* Texto deliberadamente condicional: dizer "não encontramos esta conta"
            transformaria o formulário num teste de quem é cliente do Brik. */}
        <p className="au-sub">
          Se houver uma conta com <strong>{email.trim()}</strong>, o link para criar uma nova senha
          está a caminho. Ele vale por 1 hora.
        </p>
        <p className="au-help" style={{ textAlign: 'center', marginTop: 18 }}>
          Não chegou? Confira o spam — ou volte e confira se o e-mail está certo.
        </p>
        <Link to="/login" className="au-btn" style={{ textDecoration: 'none' }}>
          Voltar para o login
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">Esqueceu a senha?</h1>
      <p className="au-sub">Diga o seu e-mail e enviamos um link para criar uma nova.</p>

      <form onSubmit={handleSubmit} noValidate>
        {erro && (
          <div className="au-alert is-danger" role="alert">
            <LuCircleAlert size={16} />
            <span>{erro}</span>
          </div>
        )}

        <div className="au-group">
          <label className="au-label" htmlFor="rec-email">E-mail</label>
          <div className="au-input-wrap">
            <input
              id="rec-email"
              className={`au-input${erro ? ' is-error' : ''}`}
              type="email"
              inputMode="email"
              placeholder="voce@suaempresa.com.br"
              value={email}
              /* Mesmo motivo do /login: preenchimento automático desligado. */
              autoComplete="off"
              autoFocus
              onChange={ev => { setEmail(ev.target.value); if (erro) setErro(null); }}
            />
          </div>
        </div>

        <button type="submit" className={`au-btn${carregando ? ' is-loading' : ''}`}>
          {carregando ? (
            <><LuLoaderCircle size={16} className="au-spin" /> Enviando…</>
          ) : (
            <><LuMail size={16} /> Enviar o link</>
          )}
        </button>
      </form>

      <p className="au-bloco-foot">
        Lembrou a senha? <Link to="/login">Voltar para o login</Link>
      </p>
    </AuthShell>
  );
};

export default EsqueciSenhaPage;
